/**
 * IndustryUpdateCuration — the report-build "which news goes in" surface.
 *
 * Lists every update available to the tenant (its own, plus global ones) and
 * lets the analyst pick and order the ones this report carries. Order of
 * `selected` IS the display order of the industry cards.
 *
 * Selection is the ONLY inclusion rule. Updates do not expire, so the same
 * standing "global context" piece can be featured in as many reports as you
 * like, and last month's update stays in the library without silently
 * re-attaching itself to this month's report. (It used to: the builder matched
 * on effective dates and anything with a blank "until" — the default — came
 * back forever, so the only workaround was deleting the old one.)
 *
 * Emits the ordered updateId[] up to the parent via onChange.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2,
  AlertTriangle,
  Plus,
  X,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Globe,
  Building2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from '../ui/Button';
import { getIndustryUpdates } from '../../services/industryUpdates';
import type { IndustryUpdate } from '../../data/types';

const CATEGORY_LABEL: Record<string, string> = {
  patch_alert: 'Patch Alert',
  trend_analysis: 'Trend Analysis',
  global_context: 'Global Context',
  best_practice: 'Best Practice',
};

interface IndustryUpdateCurationProps {
  tenantId: string;
  /** Emits the ordered set of updateIds whenever the selection changes. */
  onChange: (updateIds: string[]) => void;
}

function formatDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso.slice(0, 10);
  }
}

function ScopeTag({ update }: { update: IndustryUpdate }) {
  return update.tenantId ? (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-text-secondary"
      title="Written for this customer only"
    >
      <Building2 className="w-3 h-3" /> Tenant
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-text-secondary"
      title="Global update, available to every customer"
    >
      <Globe className="w-3 h-3" /> Global
    </span>
  );
}

export function IndustryUpdateCuration({ tenantId, onChange }: IndustryUpdateCurationProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [pool, setPool] = useState<IndustryUpdate[]>([]);
  const [selected, setSelected] = useState<IndustryUpdate[]>([]);

  // Keep the latest onChange without retriggering the emit effect.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    onChangeRef.current(selected.map((u) => u.updateId));
  }, [selected]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    // Tenant-specific updates plus every global one. No date filtering — an
    // update is a candidate for any report until it is retracted (isActive=0).
    const res = await getIndustryUpdates({ tenantId, includeGlobal: true });
    if (res.success) {
      // No prefill — nothing is featured unless the analyst picks it.
      setSelected([]);
      setPool(res.data.updates);
      setLoaded(true);
    } else {
      setError(res.error ?? 'Failed to load industry updates');
    }
    setLoading(false);
  }, [tenantId]);

  // Auto-load on tenant change so the analyst always sees what is available to
  // feature. Loading is not selecting: nothing is pre-ticked, so a report still
  // only carries news the analyst deliberately chose. (Incident curation makes
  // you click Load because its candidate query is period-scoped and heavier;
  // this is a short list and the whole point is that it is easy to overlook.)
  useEffect(() => {
    setLoaded(false);
    setSelected([]);
    setPool([]);
    setError(null);
    void load();
  }, [tenantId, load]);

  function addFromPool(u: IndustryUpdate) {
    setSelected((prev) => [...prev, u]);
    setPool((prev) => prev.filter((p) => p.updateId !== u.updateId));
  }

  function removeSelected(updateId: string) {
    setSelected((prev) => {
      const hit = prev.find((u) => u.updateId === updateId);
      if (hit) setPool((p) => [...p, hit].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom)));
      return prev.filter((u) => u.updateId !== updateId);
    });
  }

  function move(index: number, dir: -1 | 1) {
    setSelected((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-text-primary">Industry updates</h3>
          <p className="text-xs text-text-secondary mt-0.5">
            Pick the news this report carries. Optional — leave empty for no industry
            section. Updates never expire, so a standing piece can be featured again
            next month without re-writing it.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={load} disabled={loading || !tenantId}>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Reload
        </Button>
      </div>

      {error && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {loaded && (
        <>
          {selected.length === 0 ? (
            <div className="text-sm text-text-secondary border border-dashed border-border rounded-lg p-4">
              No industry updates selected — this report will have no industry section.
            </div>
          ) : (
            <ol className="space-y-2">
              {selected.map((u, idx) => (
                <li
                  key={u.updateId}
                  className="border border-border rounded-lg bg-bg-secondary p-3 flex items-start gap-3"
                >
                  <div className="flex flex-col items-center gap-1 pt-0.5">
                    <span className="text-xs font-mono text-text-secondary w-5 text-center">
                      {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="text-text-secondary hover:text-text-primary disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={idx === selected.length - 1}
                      className="text-text-secondary hover:text-text-primary disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-bg-primary border border-border text-text-secondary">
                        {CATEGORY_LABEL[u.category] ?? u.category}
                      </span>
                      <ScopeTag update={u} />
                      <span className="text-[11px] text-text-secondary">
                        {formatDate(u.effectiveFrom)}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-text-primary mt-1 truncate">
                      {u.title}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeSelected(u.updateId)}
                    className="text-text-secondary hover:text-red-600 p-1"
                    aria-label="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ol>
          )}

          {pool.length > 0 ? (
            <div className="border border-border rounded-lg p-3">
              <p className="text-xs font-medium text-text-secondary mb-2">
                Available updates — select to feature
              </p>
              <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                {pool.map((u) => (
                  <li key={u.updateId} className="flex items-center gap-2 text-sm">
                    <button
                      type="button"
                      onClick={() => addFromPool(u)}
                      className="text-brand-blue hover:opacity-80 p-1"
                      aria-label="Add to report"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-bg-primary border border-border text-text-secondary shrink-0">
                      {CATEGORY_LABEL[u.category] ?? u.category}
                    </span>
                    <span className="text-text-primary truncate flex-1">{u.title}</span>
                    <ScopeTag update={u} />
                    <span className="text-[11px] text-text-secondary shrink-0">
                      {formatDate(u.effectiveFrom)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            selected.length === 0 && (
              <div className="text-xs text-text-secondary">
                No industry updates exist for this tenant yet — write one on the
                Industry Updates page.
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
